/**
 * AppMeta Types — App definitions from backend appMetas collection
 *
 * Ported from scio2: optisigns-portal-common/libs/internal/src/lib/scio-graphql-api/appMeta/app-meta-collection.model.ts
 */

export interface AppMetaParams {
  appType?: string;
  /** Lokesh's flag: assets of this app are dashboards (Power BI, Tableau, …). Data on appMetas. */
  dashboard?: boolean;
  text?: string;
  featureKey?: string;
  requiredPlan?: string;
  keyLimitationCreateAsset?: string;
  helpLink?: string;
  limitationCreateAsset?: number;
  supportedPlans?: string[];
  minPlanRequired?: string;
  wizardClass?: string;
  isDisableSaveButton?: boolean;
  [key: string]: unknown;
}

export interface AppMetaFunc {
  name?: string;
  params: AppMetaParams;
  [key: string]: unknown;
}

export interface AppMeta {
  _id?: string;
  appType?: string;
  text?: string;
  appDescription?: string;
  image?: string;
  iconClass?: string;
  customClass?: string;
  customIcon?: string;
  thumbnail?: boolean;
  thumbnailBackgroundPrimaryColor?: string;
  order?: number;
  orderGroup?: number;
  tags?: string[];
  appCategories?: string[];
  categories?: Record<string, unknown>;
  isPopular?: boolean;
  isLocked?: boolean;
  isFree?: boolean;
  minPlanRequired?: string;
  supportedPlans?: string[];
  promotedIcon?: string;
  promoted?: boolean;
  engage?: boolean;
  showWithFeature?: string;
  disableCreateAsset?: boolean;
  allowIframe?: boolean;
  allowSmallAppIframe?: boolean;
  canAssignPlaylist?: boolean;
  canPreview?: boolean;
  /** Controls how preview renders.
   *  - `'webplayer'` (default) = in-dialog 60/40 split with live updates via postMessage
   *  - `'tab'`                 = footer "Preview" button opens `asset.webLink` in a new tab.
   *                              Only useful when the asset itself owns a public preview URL.
   *  - `'tab-generated'`       = footer "Preview" button calls the backend GraphQL
   *                              `getPreviewLink(payload)` with a synthetic dumpDevice that
   *                              references the saved asset, then opens the returned URL in
   *                              a new tab. Edit-mode only (needs `asset._id`). Use for
   *                              composites like Live TV with Ads where there's no static
   *                              `webLink` on the wrapper — scio1 parity for `getPreviewLik`.
   *  - `'playlist-contents'`   = footer "Preview" button opens a read-only modal listing the
   *                              backing playlist's items (count / total size / total time +
   *                              per-item thumbnail, name, duration). Edit-mode only, gated on
   *                              the wrapper asset's `playlistId`. Use for playlist-backed apps
   *                              (MRSS, Google Drive, Dropbox, OneDrive, contentRotation) whose
   *                              content is a sub-playlist — scio1 parity for the shared
   *                              `previewPlaylistContentRotation` (`<edit-section is-read-only>`).
   */
  previewMode?: 'webplayer' | 'tab' | 'tab-generated' | 'playlist-contents';
  canDownload?: boolean;
  canClone?: boolean;
  canExpire?: boolean;
  canMoveTo?: boolean;
  copyAssetId?: boolean;
  isOpenModalPreview?: boolean;
  scrollByParent?: boolean;
  signatureRequired?: boolean;
  authTokenRequired?: boolean;
  templateType?: number;
  func?: AppMetaFunc;
  backendUrl?: string;
  /** Base URL for manifest api.onSave/onLoad route resolution */
  apiUrl?: string;
  /** Preview iframe URL for ConfigEngineDialog */
  frontendUrl?: string;
  remoteName?: string;
  rootAppType?: string;
  isGetDetail?: boolean;
  appContent?: AppMetaContent;
  /** True when this app has a manifest in the appManifests collection. Used for routing (native config vs iframe). */
  hasManifest?: boolean;
  /**
   * Default playlist item duration in seconds when this app is added to a playlist (#1802).
   * Dashboards + WebScripting are backfilled to 60 via a deferred data migration.
   */
  playlistDefaultDuration?: number;
}

export interface AppMetaContent {
  content?: string[];
  keyPlayCount?: string;
  playCount?: number;
  countdownStepIndex?: number;
  countdownStepDuration?: number;
  stepKeep?: number;
}

/** Virtual group app IDs — these are category cards, not real apps */
export const APP_VIRTUAL = ['ms-apps', 'gg-apps'] as const;

/** Ordered plan tiers for plan comparison (lowest → highest) */
export const PLAN_TIER_ORDER = [
  'free',
  'standard',
  'pro',
  'proPlus',
  'engage',
  'enterprise',
] as const;
